// src/components/calendar/CalendarPage.js
import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import { getCalendarCreationError } from './utils/calendarCreationError';
import {
  Container,
  Box,
  Typography,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Button,
  Grid,
  CircularProgress,
  LinearProgress,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { keyframes } from '@emotion/react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseISO, isWithinInterval } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import axiosInstance from '../../config/axiosInstance';
import CalendarTable from './CalendarTable';
import { mergeWorkareaGroups } from './mergeWorkareaGroups';
import CalendarDrawer from './CalendarDrawer';
import DrawerToggleButton from './DrawerToggleButton';
import ConfirmDeleteDialog from './dialogs/ConfirmDeleteDialog';
import ConfirmInactivateDialog from './dialogs/ConfirmInactivateDialog';
import ConfirmActivateDialog from './dialogs/ConfirmActivateDialog';
import CalendarSyncProgressOverlay from '../personal/dialogs/CalendarSyncProgressOverlay';
import { StationContext } from '../context/StationContext';
import { UserContext } from '../context/UserContext';
import { CalendarContext } from '../context/CalendarContext';
import {
  hasAnyRequiredPermission,
  REPORT_PERMISSIONS,
  CALENDAR_WRITE_PERMISSIONS,
  CALENDAR_DELETE_PERMISSIONS,
} from '../../config/routePermissions';
import theme from '../../theme';
import DateSelector from './DateSelector';

const getCalendarPositionPriority = (positionName = '') => {
  const normalized = String(positionName).trim().toUpperCase();

  if (normalized.includes('PISTA')) return 0;
  if (normalized.includes('MINI')) return 1;
  return 2;
};

const sortCalendarPositions = (items = []) => (
  [...items].sort((a, b) => {
    const priorityDiff =
      getCalendarPositionPriority(a?.name) - getCalendarPositionPriority(b?.name);

    if (priorityDiff !== 0) return priorityDiff;

    return String(a?.name || '').localeCompare(String(b?.name || ''));
  })
);

const CalendarPage = () => {
  const printableRef = useRef();
  const navigate = useNavigate();

  const { selectedStation } = useContext(StationContext);
  const { user, permissions } = useContext(UserContext);
  const canCreateCalendar = hasAnyRequiredPermission(permissions, CALENDAR_WRITE_PERMISSIONS);
  const canDeleteCalendar = hasAnyRequiredPermission(permissions, CALENDAR_DELETE_PERMISSIONS);
  const canViewReports = hasAnyRequiredPermission(permissions, REPORT_PERMISSIONS);
  const isSuperAdmin = (user?.role || '').toLowerCase() === 'super_admin';

  const [positions, setPositions] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(null);

  const [datePeriods, setDatePeriods] = useState([]);
  const [currentPeriodIndex, setCurrentPeriodIndex] = useState(0);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [startDate, setStartDate] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerUserToggled, setDrawerUserToggled] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [errorText, setErrorText] = useState('');

  const [inactivateDialogOpen, setInactivateDialogOpen] = useState(false);
  const [inactivateConfirmationText, setInactivateConfirmationText] = useState('');
  const [inactivateErrorText, setInactivateErrorText] = useState('');
  const [inactivateSuccess, setInactivateSuccess] = useState(false);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [activateConfirmationText, setActivateConfirmationText] = useState('');
  const [activateErrorText, setActivateErrorText] = useState('');
  const [activateLoading, setActivateLoading] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const [loading, setLoading] = useState(false);
  const [workareas, setWorkareas] = useState([]);
  const [error, setError] = useState(null);
  const [loadingCalendar, setLoadingCalendar] = useState(true);
  const [calendarActionBusy, setCalendarActionBusy] = useState(null);
  const [creatingCalendar, setCreatingCalendar] = useState(false);
  const [stationWorkshifts, setStationWorkshifts] = useState([]);
  const [stationQuantitiesMatrix, setStationQuantitiesMatrix] = useState([]);

  // ✅ NUEVO: calendarios del mismo período para otras posiciones
  const [relatedCalendars, setRelatedCalendars] = useState([]);

  const {
    calendarData, setCalendarData,
    calendarErrors,
    calendarWarnings,
    calendarExists, setCalendarExists,
    replaceCalendarValidation,
    fetchCalendar,
    fetchCalendarValidation,
  } = useContext(CalendarContext);

  const periodLabel =
    datePeriods[currentPeriodIndex]
      ? `${datePeriods[currentPeriodIndex].start_date} - ${datePeriods[currentPeriodIndex].end_date}`
      : '';

  const mergePeriod = useCallback((periods, newPeriod) => {
    if (!newPeriod?.start_date || !newPeriod?.end_date) return periods;

    const exists = periods.some(
      (period) =>
        period.start_date === newPeriod.start_date &&
        period.end_date === newPeriod.end_date
    );
    if (exists) return periods;

    return [...periods, newPeriod].sort(
      (a, b) => parseISO(a.start_date) - parseISO(b.start_date)
    );
  }, []);

  const handlePrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `Calendario_${selectedStation?.name || 'estacion'}_${periodLabel}`,
    pageStyle: `
      @page {
        size: A4 landscape;
        margin: 5mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .no-print {
          display: none !important;
        }
        #calendar-print-area {
          transform: scale(0.85);
          transform-origin: top left;
          width: 133%;
        }
        #calendar-print-area table {
          font-size: 8px !important;
        }
        #calendar-print-area td,
        #calendar-print-area th {
          padding: 2px 4px !important;
        }
      }
    `,
  });

  const toggleDrawer = () => {
    setDrawerUserToggled(true);
    setDrawerOpen(prev => !prev);
  };

  const validateCalendarParams = ({ startDate, endDate, stationId, positionId }) => {
    const errors = [];
    if (!stationId) errors.push('Seleccioná una estación.');
    if (!positionId) errors.push('Seleccioná un área/posición.');
    if (!startDate || !endDate) {
      errors.push('Seleccioná un rango de fechas.');
    } else {
      const s = new Date(startDate);
      const e = new Date(endDate);
      if (Number.isNaN(+s) || Number.isNaN(+e)) errors.push('El rango de fechas es inválido.');
      else if (s > e) errors.push('La fecha de inicio no puede ser mayor a la de fin.');
    }
    return errors;
  };

  const updatePeriodFromDate = useCallback((periods, date) => {
    if (!periods?.length || !date) return;

    const insideIdx = periods.findIndex(({ start_date, end_date }) =>
      isWithinInterval(date, { start: parseISO(start_date), end: parseISO(end_date) })
    );

    if (insideIdx >= 0) {
      setCurrentPeriodIndex((prev) => (prev === insideIdx ? prev : insideIdx));
      return;
    }

    let prevIdx = -1;
    periods.forEach((p, idx) => {
      if (parseISO(p.end_date) < date) prevIdx = idx;
    });

    const nextIndex = prevIdx >= 0 ? prevIdx : 0;
    setCurrentPeriodIndex((prev) => (prev === nextIndex ? prev : nextIndex));
  }, []);

  useEffect(() => {
    updatePeriodFromDate(datePeriods, selectedDate);
  }, [datePeriods, selectedDate, updatePeriodFromDate]);

  const selectedPositionName = useMemo(
    () => positions.find((pos) => String(pos.id) === String(selectedPosition))?.name || '',
    [positions, selectedPosition]
  );

  const normalizeApiError = useCallback((err) => {
    if (!err?.response) {
      return {
        title: 'No se pudo conectar con el servidor.',
        detail: 'Revisá tu conexión o intentá nuevamente.',
        meta: null,
        status: 0,
      };
    }

    const { status, data } = err.response;

    const creationError = getCalendarCreationError(status, data);
    if (creationError) return creationError;

    if (status === 400 && data?.message === 'No se pudo generar el calendario solicitado') {
      const positionLabel = selectedPositionName || `ID ${data?.position ?? selectedPosition ?? ''}`.trim();
      return {
        title: `No hay empleados para el área ${positionLabel} en esta estación.`,
        detail: 'No se pudo generar el calendario porque no hay personal cargado para esa área en la estación seleccionada.',
        meta: data,
        status,
      };
    }

    return {
      title: data?.message || data?.error || 'No se pudo generar el calendario.',
      detail: data?.detalle || data?.details || '',
      meta: data,
      status,
    };
  }, [selectedPosition, selectedPositionName]);

  const loadRelatedCalendars = useCallback(async ({
    date,
    stationId,
    currentPositionId,
  }) => {
    if (!date || !stationId || !currentPositionId) {
      setRelatedCalendars([]);
      return;
    }

    const response = await axiosInstance.get('/calendar/related', {
      params: {
        date,
        station: stationId,
        exclude_position: currentPositionId,
      },
    });

    setRelatedCalendars(response?.data?.calendars || []);
  }, []);

  const handleCreateCalendar = async () => {
    setError(null);

    const currentPeriod = datePeriods[currentPeriodIndex];
    if (!currentPeriod || !selectedStation?.id || !selectedPosition) return;

    const errs = validateCalendarParams({
      startDate: currentPeriod.start_date,
      endDate: currentPeriod.end_date,
      stationId: selectedStation.id,
      positionId: selectedPosition,
    });

    if (errs.length) {
      setError({ title: 'Faltan datos para generar el calendario.', detail: errs.join('\n') });
      return;
    }

    setCreatingCalendar(true);
    setLoading(true);

    try {

      const formattedDate =
        (startDate || parseISO(currentPeriod.start_date)).toISOString().split('T')[0];

      const { data } = await axiosInstance.post(
        `/calendar/add?date=${formattedDate}&station=${encodeURIComponent(selectedStation.id)}&position=${selectedPosition}`
      );

      if (data?.json) {
        setCalendarExists(true);
        setCalendarData(data.json);
        replaceCalendarValidation({
          errors: data.errors,
          warnings: data.warnings,
        });

        const nextRange = await axiosInstance.post(
          `/date_periods/next?start_date=${currentPeriod.start_date}&end_date=${currentPeriod.end_date}`
        );

        if (nextRange.data?.data) {
          setDatePeriods((prev) => mergePeriod(prev, nextRange.data.data));
        }

        // ✅ NUEVO: recargar calendarios relacionados tras crear
        await loadRelatedCalendars({
          date: formattedDate,
          stationId: selectedStation.id,
          currentPositionId: selectedPosition,
        });

        setSnackbarMessage('Calendario creado correctamente.');
        setSnackbarOpen(true);
      }
    } catch (err) {
      setError(normalizeApiError(err));
    } finally {
      setCreatingCalendar(false);
      setLoading(false);
    }
  };

  const calendarCreationBlocked = error?.blocksCalendarCreation === true;

  const handleNextPeriod = useCallback(async () => {
    if (!datePeriods.length) return;

    if (currentPeriodIndex < datePeriods.length - 1) {
      setCurrentPeriodIndex((prev) => Math.min(prev + 1, datePeriods.length - 1));
      return;
    }

    const currentPeriod = datePeriods[currentPeriodIndex];
    if (!currentPeriod) return;

    try {
      setLoading(true);
      setError(null);

      const { data } = await axiosInstance.post(
        `/date_periods/next?start_date=${currentPeriod.start_date}&end_date=${currentPeriod.end_date}`
      );

      const newPeriod = data?.data;
      if (!newPeriod) return;

      const merged = mergePeriod(datePeriods, newPeriod);
      setDatePeriods(merged);

      const nextIndex = merged.findIndex(
        (period) =>
          period.start_date === newPeriod.start_date &&
          period.end_date === newPeriod.end_date
      );
      if (nextIndex >= 0) {
        setCurrentPeriodIndex(nextIndex);
      }
    } catch (err) {
      setError(normalizeApiError(err));
    } finally {
      setLoading(false);
    }
  }, [currentPeriodIndex, datePeriods, mergePeriod, normalizeApiError]);

  const handleDeleteCalendar = async () => {
    if (confirmationText.toLowerCase() !== 'eliminar') {
      setErrorText('Debes escribir "eliminar" para confirmar.');
      return;
    }

    try {
      setError(null);

      if (!startDate || !selectedStation?.id || !selectedPosition) return;

      const formattedDate = startDate.toISOString().split('T')[0];

      await axiosInstance.delete(
        `/calendar/delete?date=${formattedDate}&station=${encodeURIComponent(selectedStation.id)}&position=${selectedPosition}`
      );

      setCalendarExists(false);
      setCalendarData(null);
      replaceCalendarValidation({
        errors: { messages: [], items: [] },
        warnings: { messages: [], items: [] },
      });
      setRelatedCalendars([]); // ✅ NUEVO

      setDeleteDialogOpen(false);
      setConfirmationText('');
      setErrorText('');

      setSnackbarMessage('Calendario eliminado con éxito.');
      setSnackbarOpen(true);
    } catch (err) {
      setError(normalizeApiError(err));
      setErrorText('Hubo un error al intentar eliminar el calendario.');
    }
  };

  useEffect(() => {
    const safeErrors = Array.isArray(calendarErrors) ? calendarErrors : [];
    const safeWarnings = Array.isArray(calendarWarnings) ? calendarWarnings : [];
    const hasIssues = safeErrors.length > 0 || safeWarnings.length > 0;

    if (!drawerUserToggled) {
      setDrawerOpen(hasIssues);
    }
  }, [calendarErrors, calendarWarnings, drawerUserToggled]);

  useEffect(() => {
    setDrawerUserToggled(false);
  }, [currentPeriodIndex]);

  useEffect(() => {
    const fetchPositions = async () => {
      try {
        setError(null);
        const { data } = await axiosInstance.get('/positions');
        const sortedPositions = sortCalendarPositions(data.positions || []);
        setPositions(sortedPositions);
        setSelectedPosition((prev) => (
          prev && sortedPositions.some((position) => String(position.id) === String(prev))
            ? prev
            : sortedPositions[0]?.id || null
        ));
      } catch (err) {
        setError(normalizeApiError(err));
        setPositions([]);
      }
    };
    fetchPositions();
  }, [normalizeApiError]);

  useEffect(() => {
    const fetchDatePeriods = async () => {
      try {
        setError(null);

        const { data } = await axiosInstance.get('/date_periods/list');
        const periods = data.data || [];

        setDatePeriods(periods);

        if (periods.length === 0) {
          setCurrentPeriodIndex(0);
          return;
        }

        const today = new Date();

        const insideIdx = periods.findIndex(({ start_date, end_date }) =>
          isWithinInterval(today, { start: parseISO(start_date), end: parseISO(end_date) })
        );

        const nextIdx = periods.findIndex(({ start_date }) => parseISO(start_date) > today);

        setCurrentPeriodIndex(
          insideIdx >= 0
            ? insideIdx
            : (nextIdx >= 0 ? nextIdx : periods.length - 1)
        );
      } catch (err) {
        setError(normalizeApiError(err));
        setDatePeriods([]);
        setCurrentPeriodIndex(0);
      }
    };

    fetchDatePeriods();
  }, [normalizeApiError]);

  useEffect(() => {
    const fetchStationCalendarSupportData = async () => {
      if (!selectedStation?.id) {
        setStationWorkshifts([]);
        setStationQuantitiesMatrix([]);
        return;
      }

      try {
        const [workshiftsResponse, quantitiesResponse] = await Promise.all([
          axiosInstance.get(`/station/${selectedStation.id}/workshifts?include_disabled=true`),
          axiosInstance.get(`/station/${selectedStation.id}/workshifts/quantities?include_disabled=false`),
        ]);

        setStationWorkshifts(Array.isArray(workshiftsResponse?.data) ? workshiftsResponse.data : []);
        setStationQuantitiesMatrix(Array.isArray(quantitiesResponse?.data) ? quantitiesResponse.data : []);
      } catch (e) {
        console.error('Error al cargar soporte de turnos/cantidades de estación:', e);
        setStationWorkshifts([]);
        setStationQuantitiesMatrix([]);
      }
    };

    fetchStationCalendarSupportData();
  }, [selectedStation?.id]);

  const clearPlannerView = useCallback(() => {
    setCalendarExists(false);
    setCalendarData(null);
    replaceCalendarValidation({
      errors: { messages: [], items: [] },
      warnings: { messages: [], items: [] },
    });
    setRelatedCalendars([]);
  }, [replaceCalendarValidation, setCalendarData, setCalendarExists]);

  useEffect(() => {
    (async () => {
      if (
        !datePeriods.length ||
        currentPeriodIndex < 0 ||
        currentPeriodIndex >= datePeriods.length ||
        !selectedStation?.id ||
        !selectedPosition
      ) {
        clearPlannerView();
        return;
      }

      const currentPeriod = datePeriods[currentPeriodIndex];
      const newStartDate = parseISO(currentPeriod.start_date);
      const formattedDate = newStartDate.toISOString().split('T')[0];

      setStartDate(newStartDate);

      const errs = validateCalendarParams({
        startDate: currentPeriod.start_date,
        endDate: currentPeriod.end_date,
        stationId: selectedStation.id,
        positionId: selectedPosition,
      });

      if (errs.length) {
        clearPlannerView();
        setError({ title: 'Faltan datos para generar el calendario.', detail: errs.join('\n') });
        return;
      }

      setLoadingCalendar(true);
      setError(null);
      clearPlannerView();

      try {
        await fetchCalendar({
          date: formattedDate,
          station: selectedStation.id,
          position: selectedPosition,
          loadValidation: false,
        });

        // El calendario ya puede mostrarse. Las consultas secundarias no deben
        // mantener bloqueada la tabla mientras cargan advertencias.
        setLoadingCalendar(false);

        // Pedir además los demás calendarios y la validación, sin bloquear la
        // primera visualización del calendario principal.
        Promise.all([
          loadRelatedCalendars({
            date: formattedDate,
            stationId: selectedStation.id,
            currentPositionId: selectedPosition,
          }),
          fetchCalendarValidation({
            date: formattedDate,
            station: selectedStation.id,
            position: selectedPosition,
          }),
        ]).catch((secondaryError) => {
          console.error('Error al cargar información secundaria del calendario:', secondaryError);
        });
      } catch (e) {
        setError(normalizeApiError(e));
        setRelatedCalendars([]);
      } finally {
        setLoadingCalendar(false);
      }
    })();
  }, [
    currentPeriodIndex,
    datePeriods,
    selectedStation,
    selectedPosition,
    clearPlannerView,
    fetchCalendar,
    fetchCalendarValidation,
    loadRelatedCalendars,
    normalizeApiError,
  ]);

  useEffect(() => {
    setWorkareas(calendarData?.workareas || []);
  }, [calendarData]);

  // Un calendario ya viene acotado a una sola posición, así que todos sus
  // grupos de workarea (ej. CAJA y REPONEDOR bajo MINI) son fusionables en
  // una sola tabla -- ver mergeWorkareaGroups.js.
  const mergedWorkarea = useMemo(() => mergeWorkareaGroups(workareas), [workareas]);

  const buildReportsUrl = useCallback(() => {
    const p = datePeriods[currentPeriodIndex];
    if (!p || !selectedStation?.id || !selectedPosition) return '/reportes';

    const params = new URLSearchParams({
      station: String(selectedStation.id),
      position: String(selectedPosition),
      start_date: String(p.start_date),
      end_date: String(p.end_date),
      report: 'employee_activity',
    });

    return `/reportes?${params.toString()}`;
  }, [datePeriods, currentPeriodIndex, selectedStation?.id, selectedPosition]);

  const handleGoReports = useCallback(() => {
    navigate(buildReportsUrl());
  }, [navigate, buildReportsUrl]);

  const handleInactivateCalendar = async () => {
    if (inactivateConfirmationText.toLowerCase() !== 'cerrar') {
      setInactivateErrorText('Debes escribir "cerrar" para confirmar.');
      return;
    }

    try {
      setError(null);

      const currentPeriod = datePeriods[currentPeriodIndex];
      if (!currentPeriod || !selectedStation?.id || !selectedPosition) {
        setInactivateErrorText('Faltan datos para cerrar el calendario.');
        return;
      }

      const payload = {
        station: selectedStation.id,
        position: selectedPosition,
        start_date: currentPeriod.start_date,
        end_date: currentPeriod.end_date,
      };

      const response = await axiosInstance.post('calendar/inactivate', payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      void response;

      const formattedDate = (startDate || parseISO(currentPeriod.start_date))
        .toISOString()
        .split('T')[0];

      await fetchCalendar({
        date: formattedDate,
        station: selectedStation.id,
        position: selectedPosition,
      });

      await loadRelatedCalendars({
        date: formattedDate,
        stationId: selectedStation.id,
        currentPositionId: selectedPosition,
      });

      setInactivateSuccess(true);
      setInactivateErrorText('');

      setSnackbarMessage('Calendario cerrado correctamente.');
      setSnackbarOpen(true);
    } catch (err) {
      const apiErr = normalizeApiError(err);
      setError(apiErr);
      setInactivateErrorText(apiErr?.title || 'No se pudo cerrar el calendario.');
    }
  };

  const handleOpenInactivate = () => {
    setInactivateErrorText('');
    setInactivateConfirmationText('');
    setInactivateSuccess(false);
    setInactivateDialogOpen(true);
  };

  const handleCloseInactivate = () => {
    setInactivateDialogOpen(false);
    setInactivateConfirmationText('');
    setInactivateErrorText('');
    setInactivateSuccess(false);
  };

  const handleActivateCalendar = async () => {
    if (!isSuperAdmin) return;
    if (activateConfirmationText.toLowerCase() !== 'reactivar') {
      setActivateErrorText('Debes escribir "reactivar" para confirmar.');
      return;
    }

    const currentPeriod = datePeriods[currentPeriodIndex];
    if (!currentPeriod || !selectedStation?.id || !selectedPosition) {
      setActivateErrorText('Faltan datos para reactivar el calendario.');
      return;
    }

    try {
      setActivateLoading(true);
      setError(null);
      await axiosInstance.post('calendar/activate', {
        station: selectedStation.id,
        position: selectedPosition,
        start_date: currentPeriod.start_date,
        end_date: currentPeriod.end_date,
      });

      const formattedDate = (startDate || parseISO(currentPeriod.start_date))
        .toISOString()
        .split('T')[0];
      await fetchCalendar({ date: formattedDate, station: selectedStation.id, position: selectedPosition });
      await loadRelatedCalendars({
        date: formattedDate,
        stationId: selectedStation.id,
        currentPositionId: selectedPosition,
      });

      setActivateDialogOpen(false);
      setActivateConfirmationText('');
      setActivateErrorText('');
      setSnackbarMessage('Calendario reactivado correctamente.');
      setSnackbarOpen(true);
    } catch (err) {
      const apiErr = normalizeApiError(err);
      setError(apiErr);
      setActivateErrorText(apiErr?.title || 'No se pudo reactivar el calendario.');
    } finally {
      setActivateLoading(false);
    }
  };

  const handleOpenActivate = () => {
    if (!isSuperAdmin) return;
    setActivateErrorText('');
    setActivateConfirmationText('');
    setActivateDialogOpen(true);
  };

  const handleCloseActivate = () => {
    if (activateLoading) return;
    setActivateDialogOpen(false);
    setActivateConfirmationText('');
    setActivateErrorText('');
  };

  const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  `;

  const { auxAreasByCI, primaryAreaByCI } = useMemo(() => {
    const auxMap = {};
    const primaryMap = {};
    const meta = calendarData?.employees_meta || {};

    Object.entries(meta).forEach(([ci, info]) => {
      const primary = info?.primary_area?.name || null;
      const auxList = Array.isArray(info?.aux_areas)
        ? info.aux_areas.map(a => a?.name).filter(Boolean)
        : [];

      if (primary) primaryMap[ci] = primary;
      if (auxList.length) auxMap[ci] = auxList;
    });

    return { auxAreasByCI: auxMap, primaryAreaByCI: primaryMap };
  }, [calendarData?.employees_meta]);

  // ✅ NUEVO: calendario actual + calendarios relacionados para cálculos multirol
  const allCalendarsForPeriod = useMemo(() => {
    const calendars = [];

    if (calendarData) {
      calendars.push({
        position_id: selectedPosition,
        position_name: positions.find((p) => String(p.id) === String(selectedPosition))?.name || '',
        calendar_data: calendarData,
      });
    }

    relatedCalendars.forEach((item) => {
      if (!item?.calendar_data) return;

      const alreadyIncluded = calendars.some(
        (c) => String(c.position_id) === String(item.position_id)
      );

      if (!alreadyIncluded) {
        calendars.push(item);
      }
    });

    return calendars;
  }, [calendarData, relatedCalendars, selectedPosition, positions]);

  const isCalendarClosed = Boolean(calendarExists && calendarData && calendarData.status !== 'active');

  // Tras un ajuste rápido de dotación desde el pie, se recarga el calendario para ver
  // los esperados y las advertencias actualizados.
  const handleStaffingChanged = async () => {
    if (!calendarData?.start_date || !selectedStation?.id || !selectedPosition) return;
    await fetchCalendar({
      date: calendarData.start_date,
      station: selectedStation.id,
      position: selectedPosition,
    });
  };
  const showCalendarBusy = Boolean(calendarActionBusy) && !loading && !loadingCalendar;
  const busyTitle = calendarActionBusy?.actionLabel || 'Guardando cambios del calendario';
  const busyDetail = [
    calendarActionBusy?.shiftName ? `Turno ${calendarActionBusy.shiftName}` : null,
    calendarActionBusy?.employeeName || null,
    calendarActionBusy?.scope === 'period'
      ? `Quincena ${calendarActionBusy?.day || ''}`
      : calendarActionBusy?.day
        ? `Día ${calendarActionBusy.day}`
        : null,
  ].filter(Boolean).join(' · ');

  const pageBg = isCalendarClosed ? 'rgba(255, 152, 0, 0.06)' : '#fafbfc';
  const headerBg = isCalendarClosed ? 'rgba(255, 152, 0, 0.10)' : 'transparent';
  const tableWrapperSx = isCalendarClosed
    ? { filter: 'grayscale(0.15)', opacity: 0.96 }
    : {};

  const errorsCount = Array.isArray(calendarErrors) ? calendarErrors.length : 0;
  const warningsCount = Array.isArray(calendarWarnings) ? calendarWarnings.length : 0;

  const canPrint = calendarExists && calendarData && !loading && !loadingCalendar;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container
        maxWidth={false}
        disableGutters
        sx={{ minHeight: '100vh', p: 1, background: pageBg }}
      >
        <Box sx={{ px: { xs: 1, sm: 2, md: 5, lg: 8 }, py: { xs: 1, md: 3 }, width: '100%' }} />

        <DrawerToggleButton
          open={drawerOpen}
          onToggle={toggleDrawer}
          errorsCount={errorsCount}
          warningsCount={warningsCount}
        />

        <Tooltip title={canPrint ? 'Imprimir calendario' : 'No hay calendario para imprimir'}>
          <span>
            <IconButton
              onClick={handlePrint}
              disabled={!canPrint}
              className="no-print"
              sx={{
                position: 'fixed',
                top: '90px',
                left: 20,
                zIndex: 1300,
                backgroundColor: 'white',
                borderRadius: '50%',
                boxShadow: 3,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  transform: 'scale(1.2)',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'white',
                  opacity: 0.4,
                },
              }}
            >
              <PrintIcon sx={{ fontSize: '2.5rem', color: canPrint ? 'primary.main' : 'grey.400' }} />
            </IconButton>
          </span>
        </Tooltip>

        {isCalendarClosed && (
          <Box className="no-print" sx={{ px: { xs: 1, sm: 2, md: 5, lg: 8 }, mb: 2 }}>
            <Alert
              severity="warning"
              sx={{
                borderRadius: 2,
                bgcolor: 'rgba(255,152,0,0.12)',
                border: '1px solid rgba(255,152,0,0.35)',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Calendario cerrado (solo lectura)
              </Typography>
              <Typography variant="body2">
                {canViewReports
                  ? 'Este período está bloqueado para edición. Podés consultarlo o ir a Reportes.'
                  : 'Este período está bloqueado para edición. Solo podés consultarlo.'}
              </Typography>
            </Alert>
          </Box>
        )}

        {error && (
          <Box className="no-print" sx={{ px: { xs: 1, sm: 2, md: 5, lg: 8 }, mb: 2 }}>
            <Alert
              severity={error.severity || 'error'}
              sx={{
                borderRadius: 2,
                bgcolor: error.severity === 'warning'
                  ? 'rgba(255,152,0,0.12)'
                  : 'rgba(211,47,47,0.08)',
                border: error.severity === 'warning'
                  ? '1px solid rgba(255,152,0,0.35)'
                  : '1px solid rgba(211,47,47,0.25)',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {error.title}
              </Typography>
              {error.detail && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {error.detail}
                </Typography>
              )}
            </Alert>
          </Box>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: drawerOpen ? 'minmax(0,1fr) 360px' : 'minmax(0,1fr)',
            width: '100%',
            minHeight: '70vh',
            transition: 'grid-template-columns 0.3s',
          }}
        >
          <Box
            ref={printableRef}
            id="calendar-print-area"
            sx={{ width: '100%', display: 'flex', flexDirection: 'column', minWidth: 0, overflowX: 'auto' }}
          >
            <Box
              sx={{
                textAlign: 'center',
                mb: 2,
                pt: 2,
                pb: 1.5,
                bgcolor: headerBg,
                borderRadius: 2,
                mx: { xs: 1, sm: 2, md: 5, lg: 8 },
              }}
            >
              <Typography variant="h4">Planificador</Typography>
              <Typography variant="subtitle1">
                Estación: {selectedStation?.name || 'Cargando...'}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                  display: 'none',
                  '@media print': { display: 'block' },
                }}
              >
                Período: {periodLabel}
              </Typography>
            </Box>

            <Box
              className="no-print"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                mb: 3,
                px: { xs: 1, sm: 2, md: 5, lg: 8 },
              }}
            >
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                <DateSelector
                  datePeriods={datePeriods}
                  currentPeriodIndex={currentPeriodIndex}
                  onPeriodChange={setCurrentPeriodIndex}
                  onNextPeriod={handleNextPeriod}
                  selectedDate={selectedDate}
                  onDateChange={setSelectedDate}
                  loading={loading}
                  disabled={loading || showCalendarBusy}
                />
              </Box>

              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                {calendarExists && calendarData && calendarData.status === 'active' && canDeleteCalendar && (
                  <>
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      disabled={loading || loadingCalendar || showCalendarBusy}
                      onClick={handleOpenInactivate}
                    >
                      Cerrar calendario
                    </Button>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#d32f2f',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        '&:hover': { color: '#b71c1c' },
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                      }}
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      Eliminar calendario
                    </Typography>
                  </>
                )}

                {calendarExists && calendarData && calendarData.status !== 'active' && (
                  <>
                    <Chip label="Calendario cerrado" size="small" />
                    {isSuperAdmin && (
                      <Button
                        variant="outlined"
                        color="success"
                        size="small"
                        disabled={loading || loadingCalendar || showCalendarBusy || activateLoading}
                        onClick={handleOpenActivate}
                      >
                        Reactivar calendario
                      </Button>
                    )}
                    {canViewReports && (
                      <Button variant="outlined" size="small" onClick={handleGoReports}>
                        Ir a reportes
                      </Button>
                    )}
                  </>
                )}
              </Box>

              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <Select
                  value={selectedPosition || ''}
                  onChange={(e) => setSelectedPosition(e.target.value)}
                  size="small"
                  sx={{ minWidth: 150, textAlign: 'center' }}
                  disabled={loading || showCalendarBusy}
                >
                  {positions.map((pos) => (
                    <MenuItem key={pos.id} value={pos.id}>
                      {pos.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>

            <Box sx={{ width: '100%' }}>
              {showCalendarBusy && (
                <Box
                  className="no-print"
                  sx={{
                    position: 'sticky',
                    top: 64,
                    zIndex: 5,
                    mx: { xs: 1, sm: 2, md: 5, lg: 8 },
                    mb: 2,
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: '0 10px 24px rgba(0, 35, 57, 0.10)',
                    border: '1px solid rgba(0, 35, 57, 0.10)',
                    bgcolor: 'rgba(255,255,255,0.96)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                    <LinearProgress color="secondary" />
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                    >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {busyTitle}
                      </Typography>
                      {busyDetail && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {busyDetail}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      size="small"
                      color="secondary"
                      label={calendarActionBusy?.scope === 'period' ? 'Aplicando quincena' : 'Guardando'}
                      icon={<CircularProgress size={14} color="inherit" />}
                    />
                  </Box>
                </Box>
              )}

              {loading || loadingCalendar ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 200,
                  }}
                >
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography variant="body1">Cargando calendario...</Typography>
                </Box>
              ) : workareas.length > 0 ? (
                <Box
                  sx={{
                    position: 'relative',
                    animation: `${fadeIn} 0.4s ease-in-out`,
                    pointerEvents: showCalendarBusy ? 'none' : 'auto',
                    opacity: showCalendarBusy ? 0.96 : 1,
                    ...tableWrapperSx,
                  }}
                >
                  {mergedWorkarea && (
                    <Box sx={{ mb: 4 }}>
                      <Box sx={{ textAlign: 'center', mb: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
                          {(selectedPositionName || mergedWorkarea.workarea_name || `Área ${mergedWorkarea.workarea_id}`).toUpperCase()}
                        </Typography>
                      </Box>

                      <Grid container sx={{ width: '100%', overflowX: 'auto' }}>
                        <Grid item xs={12}>
                          {calendarExists && calendarData ? (
                            <CalendarTable
                              calendarData={{
                                days: mergedWorkarea.days,
                                station_id: calendarData.station,
                                workarea_id: mergedWorkarea.workarea_id,
                                workarea_name: mergedWorkarea.workarea_name,
                                row_order: mergedWorkarea.row_order || [],
                                workareas: calendarData?.workareas || [],
                              }}
                              combinedWorkareaIds={mergedWorkarea.workarea_ids}
                              allCalendarsForPeriod={allCalendarsForPeriod} // ✅ NUEVO
                              stationWorkshifts={stationWorkshifts}
                              stationQuantitiesMatrix={stationQuantitiesMatrix}
                              auxAreasByCI={auxAreasByCI}
                              primaryAreaByCI={primaryAreaByCI}
                              readOnly={isCalendarClosed}
                              onStaffingChanged={handleStaffingChanged}
                              onBusyChange={setCalendarActionBusy}
                              busyState={calendarActionBusy}
                            />
                          ) : (
                            <Box sx={{ textAlign: 'center', mt: 12 }}>
                              <Typography variant="h6" sx={{ mb: 3 }}>
                                No se encontró un calendario para este período.
                              </Typography>
                              <Button
                                onClick={handleCreateCalendar}
                                variant="contained"
                                disabled={loading || creatingCalendar}
                                sx={{
                                  backgroundColor: theme.palette.primary.main,
                                  color: '#fff',
                                  '&:hover': { backgroundColor: theme.palette.primary.dark },
                                }}
                              >
                                {calendarCreationBlocked ? 'Volver a intentar' : 'Crear Calendario'}
                              </Button>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', mt: 12 }}>
                  <Typography variant="h6" sx={{ mb: 3 }}>
                    No se encontró un calendario para este período.
                  </Typography>
                  {canCreateCalendar && (
                    <Button
                      onClick={handleCreateCalendar}
                      variant="contained"
                      disabled={loading || loadingCalendar || creatingCalendar}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                        '&:hover': { backgroundColor: theme.palette.primary.dark },
                      }}
                    >
                      {calendarCreationBlocked ? 'Volver a intentar' : 'Crear Calendario'}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {drawerOpen && (
            <Box sx={{ minWidth: 320, maxWidth: 400, width: 360, overflowY: 'auto' }}>
              <CalendarDrawer drawerOpen={drawerOpen} loading={loading} toggleDrawer={toggleDrawer} />
            </Box>
          )}
        </Box>

        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDeleteCalendar}
          confirmationText={confirmationText}
          setConfirmationText={setConfirmationText}
          errorText={errorText}
        />

        <ConfirmInactivateDialog
          open={inactivateDialogOpen}
          onClose={handleCloseInactivate}
          onConfirm={handleInactivateCalendar}
          confirmationText={inactivateConfirmationText}
          setConfirmationText={setInactivateConfirmationText}
          errorText={inactivateErrorText}
          stationName={selectedStation?.name}
          positionName={selectedPositionName}
          periodLabel={periodLabel}
          loading={false}
          success={inactivateSuccess}
          onGoReports={canViewReports ? () => {
            handleCloseInactivate();
            handleGoReports();
          } : undefined}
        />

        <ConfirmActivateDialog
          open={activateDialogOpen}
          onClose={handleCloseActivate}
          onConfirm={handleActivateCalendar}
          confirmationText={activateConfirmationText}
          setConfirmationText={setActivateConfirmationText}
          errorText={activateErrorText}
          stationName={selectedStation?.name}
          positionName={selectedPositionName}
          periodLabel={periodLabel}
          loading={activateLoading}
        />

        <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}>
          <Alert severity="success">{snackbarMessage || 'Operación realizada con éxito.'}</Alert>
        </Snackbar>

        <CalendarSyncProgressOverlay open={creatingCalendar} variant="plannerCreate" />
      </Container>
    </LocalizationProvider>
  );
};

export default CalendarPage;
